/**
 * Prev/next arrows + small behaviour fixes for Horizon's PDP zoom dialog.
 *
 * The native zoom dialog is swipe/scroll-only and its markup lives in a base
 * Horizon snippet (product-media-gallery-content.liquid) that must not be
 * edited, so everything here is layered on from outside:
 *   - injects prev/next arrows wired to scroll the slide track one viewport
 *     at a time, positioned off the bordered stage's edges (the stage is
 *     stationary, so the arrows stay put while slides move inside it)
 *   - neutralizes drag-zoom-wrapper's 1.5× default mobile zoom, which was
 *     sized for the old full-bleed dialog and crops the boxed stage
 *   - enables the shared mouse drag-to-scroll util on the thumbnail row
 *   - re-syncs the selected thumbnail as the track scrolls: Horizon's own
 *     zoom-dialog.js does this too, but listens for a `scroll` event on the
 *     <dialog> element — correct for its native full-bleed layout, where the
 *     dialog itself was the scroll container. Here the track scrolls
 *     internally instead (see atomberg-pdp-zoom.css's stage), and scroll
 *     events don't bubble to ancestors, so that listener never fires
 * Styled by atomberg-pdp-zoom.css.
 */
(function () {
  if (/** @type {any} */ (window)._atombergPdpZoomInit) return;
  /** @type {any} */ (window)._atombergPdpZoomInit = true;

  var ARROW_INSET = 12;

  var CHEVRON =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  /**
   * @param {string} dir
   * @param {string} label
   * @returns {HTMLButtonElement}
   */
  function makeArrow(dir, label) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'atomberg-zoom-arrow atomberg-zoom-arrow--' + dir;
    btn.setAttribute('aria-label', label);
    btn.innerHTML = CHEVRON;
    return btn;
  }

  /** @param {HTMLElement} dialog */
  function initDialog(dialog) {
    if (dialog.dataset.atombergZoomArrows) return;
    var trackMaybe = /** @type {HTMLElement|null} */ (
      dialog.querySelector('.dialog-zoomed-gallery')
    );
    if (!trackMaybe || trackMaybe.children.length < 2) return;
    var track = trackMaybe;
    dialog.dataset.atombergZoomArrows = '1';

    var prev = makeArrow('prev', 'Previous image');
    var next = makeArrow('next', 'Next image');

    /* This track is a plain scrollable list (not a Horizon slideshow-
       component), so looping at the boundaries is our own responsibility —
       matches the wrap the native slideshow's arrows/drag get from
       atomberg-slideshow-infinite-drag.js elsewhere in the theme. */
    prev.addEventListener('click', function () {
      var max = track.scrollWidth - track.clientWidth;
      if (max > 0 && track.scrollLeft <= 4) {
        track.scrollTo({ left: max, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });
      }
    });
    next.addEventListener('click', function () {
      var max = track.scrollWidth - track.clientWidth;
      if (max > 0 && track.scrollLeft >= max - 4) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });
      }
    });

    /* Swipe-to-wrap: native touch scrolling can't go past its own bounds, so
       a swipe that starts and ends on a boundary slide (same detection as
       atomberg-slideshow-infinite-drag.js) manually jumps to the other end
       instead of just rubber-banding in place. */
    /** @type {{x: number, y: number, atStart: boolean, atEnd: boolean} | null} */
    var dragGesture = null;
    track.addEventListener('pointerdown', function (e) {
      var max = track.scrollWidth - track.clientWidth;
      dragGesture = {
        x: e.clientX,
        y: e.clientY,
        atStart: track.scrollLeft <= 4,
        atEnd: max > 0 && track.scrollLeft >= max - 4,
      };
    });
    track.addEventListener('pointerup', function (e) {
      if (!dragGesture) return;
      var g = dragGesture;
      dragGesture = null;
      var dx = e.clientX - g.x;
      var dy = e.clientY - g.y;
      if (Math.abs(dx) < 30 || Math.abs(dx) <= Math.abs(dy)) return;
      var wantsNext = dx < 0 && g.atEnd;
      var wantsPrev = dx > 0 && g.atStart;
      if (!wantsNext && !wantsPrev) return;
      // setTimeout 0: let the native scroll-snap settle from this gesture
      // first, then re-check the boundary before deciding to wrap.
      setTimeout(function () {
        var max = track.scrollWidth - track.clientWidth;
        if (wantsNext && track.scrollLeft >= max - 4) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else if (wantsPrev && track.scrollLeft <= 4) {
          track.scrollTo({ left: max, behavior: 'smooth' });
        }
      }, 0);
    });

    function positionArrows() {
      /* Anchored to the stage (the bordered track box), not the current
         image — the frame is stationary, so the arrows stay put while
         slides move inside it. Inset from the stage's own edges (overlaid
         on the image, like arrows_on_media elsewhere in this theme) rather
         than sitting outside it — outside-with-a-gap has no room on narrow
         viewports and used to collapse to the screen edge instead. */
      var rect = track.getBoundingClientRect();
      if (rect.width === 0) return;
      // Measured, not a constant: the button's size comes from the shared
      // --atomberg-arrow-* padding/icon tokens (atomberg-global.css), which
      // differ between the desktop and mobile media query.
      var arrowWidth = next.getBoundingClientRect().width;
      var mid = rect.top + rect.height / 2;
      prev.style.top = mid + 'px';
      next.style.top = mid + 'px';
      prev.style.left = rect.left + ARROW_INSET + 'px';
      next.style.left = rect.right - ARROW_INSET - arrowWidth + 'px';
    }

    var lastActiveIndex = -1;

    function syncActiveThumbnail() {
      var index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      var thumbs = dialog.querySelectorAll('.dialog-thumbnails-list__thumbnail');
      thumbs.forEach(function (thumb, i) {
        thumb.setAttribute('aria-selected', String(i === index));
      });
      if (index === lastActiveIndex) return;
      lastActiveIndex = index;
      // Dialog closed: the thumbnail row has no layout box (see the rects
      // comment above), so scrollIntoView finds no local scroll container
      // to target and falls back to scrolling the page itself. Only sync
      // once the dialog actually has geometry to scroll within.
      if (!dialog.hasAttribute('open')) return;
      /* Keep the active thumbnail in view within its own scrollable row —
         same reasoning as the arrows: Horizon's native scroll-into-view for
         this lived in the <dialog>-scoped handleScroll that no longer fires
         (see the file header comment). block: 'nearest' keeps this from
         touching any vertical scroll position. */
      var active = thumbs[index];
      if (active) active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    function update() {
      var max = track.scrollWidth - track.clientWidth;
      // Both wrap at the boundaries now, so only disable when there's
      // truly nowhere to go — a single image, nothing to scroll between.
      prev.disabled = max <= 0;
      next.disabled = max <= 0;
      positionArrows();
      syncActiveThumbnail();
    }

    var rafId = 0;
    function scheduleUpdate() {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    }

    function neutralizeDefaultZoom() {
      /* drag-zoom-wrapper.js defaults its mobile scale to 1.5× via inline
         CSS vars — sized for the old full-bleed dialog, it crops the boxed
         stage badly. Reset inline on every open (Horizon's own close-event
         reset re-applies 1.5). The wrapper's internal state still starts at
         1.5, so the first pinch jumps slightly — acceptable, since patching
         the base Horizon JS is off-limits. */
      dialog.querySelectorAll('drag-zoom-wrapper').forEach(function (el) {
        if (el instanceof HTMLElement) {
          el.style.setProperty('--drag-zoom-scale', '1');
          el.style.setProperty('--drag-zoom-translate-x', '0px');
          el.style.setProperty('--drag-zoom-translate-y', '0px');
        }
      });
    }

    track.addEventListener('scroll', scheduleUpdate);
    window.addEventListener('resize', scheduleUpdate);

    /* Rects are all 0 while the dialog is closed, so arrows positioned at
       init time land off-screen — the reason they only appeared after the
       first slide (whose scroll event re-measured). Re-measure on every
       open, whatever triggered it (zoom button, slide click, JS). */
    function handleOpen() {
      neutralizeDefaultZoom();
      scheduleUpdate();
      /* Once more after Horizon's open view-transition settles — the stage
         rect isn't final until the transition finishes. */
      setTimeout(update, 350);
    }

    new MutationObserver(function () {
      if (dialog.hasAttribute('open')) handleOpen();
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    if (dialog.hasAttribute('open')) handleOpen();

    var dragScroll = /** @type {any} */ (window).AtombergDragScroll;
    var thumbs = /** @type {HTMLElement|null} */ (dialog.querySelector('.dialog-thumbnails-list'));
    if (dragScroll && thumbs) dragScroll.enable(thumbs);

    dialog.append(prev, next);
    update();
  }

  function initAll() {
    document.querySelectorAll('zoom-dialog dialog').forEach(function (dialog) {
      if (dialog instanceof HTMLElement) initDialog(dialog);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Capture phase: variant changes morph the media-gallery section and
     replace the dialog, dropping the injected arrows and observers. Any
     click inside the gallery (slide, zoom button, thumbnail — all possible
     open triggers) re-runs init before Horizon's own bubble-phase open
     handler; already-initialized dialogs bail on the dataset guard. */
  document.addEventListener(
    'click',
    function (e) {
      if (e.target instanceof Element && e.target.closest('media-gallery')) {
        initAll();
      }
    },
    true
  );
})();
